/**
 * Camera and microphone access, and what to tell people when it fails
 * (E3-S1, E3-S2). The decision logic is pure so every permission outcome is
 * covered by unit tests; only `acquire` touches the browser.
 */

/** What happened when the app asked for a device. */
export type DeviceState =
  | 'ok'
  | 'off' // The person chose not to use it.
  | 'denied' // Permission was refused or dismissed.
  | 'inUse' // Another app or tab holds the device.
  | 'notFound' // No such device is attached.
  | 'unavailable'; // The browser cannot offer devices here at all.

export type JoinMode = 'full' | 'audioOnly' | 'videoOnly' | 'listenOnly';

export interface Acquired {
  audioTrack: MediaStreamTrack | null;
  videoTrack: MediaStreamTrack | null;
  mic: DeviceState;
  cam: DeviceState;
}

export interface AcquireOptions {
  wantAudio: boolean;
  wantVideo: boolean;
  micId?: string;
  camId?: string;
  /** 320×240 for the pre-join preview; call quality otherwise. */
  preview?: boolean;
  hd?: boolean;
  facingMode?: 'user' | 'environment';
}

type FailedState = Exclude<DeviceState, 'ok' | 'off'>;

export function supportsCalls(): boolean {
  return typeof RTCPeerConnection !== 'undefined' && typeof WebSocket !== 'undefined';
}

export function hasMediaDevices(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function';
}

/** Maps the many names browsers use for getUserMedia failures onto four causes. */
export function classifyMediaError(err: unknown): FailedState {
  const name = typeof err === 'object' && err !== null && 'name' in err ? String((err as { name: unknown }).name) : '';
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'inUse';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'notFound';
    case 'TypeError':
      return 'unavailable';
    default:
      return 'inUse';
  }
}

export function audioConstraints(micId?: string): MediaTrackConstraints {
  return {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    ...(micId ? { deviceId: { exact: micId } } : {}),
  };
}

export function videoConstraints(opts: Pick<AcquireOptions, 'camId' | 'preview' | 'hd' | 'facingMode'>): MediaTrackConstraints {
  const size = opts.preview ? { width: 320, height: 240, fps: 15 } : opts.hd ? { width: 1280, height: 720, fps: 30 } : { width: 640, height: 360, fps: 24 };
  return {
    width: { ideal: size.width },
    height: { ideal: size.height },
    frameRate: { ideal: size.fps, max: 30 },
    ...(opts.camId ? { deviceId: { exact: opts.camId } } : { facingMode: opts.facingMode ?? 'user' }),
  };
}

/**
 * The sizes the camera is sent in (simulcast, E6-S2): about 180p and 360p,
 * and 720p as well only when the meeting's plan includes HD and the person
 * wants it. Worked out from the size the camera actually delivers (which can
 * be larger, or smaller, than the one asked for), so a meeting without HD
 * never gets more than 360p from this app whatever the camera does.
 */
export function cameraEncodings(size: { width?: number; height?: number }, hd: boolean): RTCRtpEncodingParameters[] {
  const short = Math.min(size.width ?? 640, size.height ?? 360);
  const scaleFor = (lines: number) => Math.max(1, short / lines);
  const encodings: RTCRtpEncodingParameters[] = [
    { rid: 'r0', scaleResolutionDownBy: scaleFor(180), maxBitrate: 100_000, maxFramerate: 15 },
    { rid: 'r1', scaleResolutionDownBy: scaleFor(360), maxBitrate: 300_000, maxFramerate: 20 },
  ];
  if (hd && short >= 700) encodings.push({ rid: 'r2', scaleResolutionDownBy: scaleFor(720), maxBitrate: 900_000, maxFramerate: 30 });
  return encodings;
}

type GetUserMedia = (constraints: MediaStreamConstraints) => Promise<MediaStream>;

/** How long a device that would not start is given before it is asked once more. */
export const BUSY_RETRY_MS = 1000;

async function one(gum: GetUserMedia, kind: 'audio' | 'video', constraints: MediaTrackConstraints): Promise<{ track: MediaStreamTrack | null; state: DeviceState }> {
  const attempt = async (c: MediaTrackConstraints) => {
    try {
      const stream = await gum(kind === 'audio' ? { audio: c } : { video: c });
      return (kind === 'audio' ? stream.getAudioTracks() : stream.getVideoTracks())[0] ?? null;
    } catch (err) {
      // The browser's own words, for whoever looks into a device that would not start.
      console.warn(`The ${kind === 'audio' ? 'microphone' : 'camera'} could not be started: ${(err as Error)?.name}: ${(err as Error)?.message}`);
      throw err;
    }
  };
  try {
    return { track: await attempt(constraints), state: 'ok' };
  } catch (err) {
    const name = (err as { name?: string }).name;
    // The remembered device was unplugged: fall back to the default one (E4-S8).
    if ((name === 'OverconstrainedError' || name === 'NotFoundError') && 'deviceId' in constraints) {
      const { deviceId: _gone, ...rest } = constraints;
      try {
        return { track: await attempt(rest), state: 'ok' };
      } catch (again) {
        return { track: null, state: classifyMediaError(again) };
      }
    }
    // A camera let go of a moment ago (Rejoin after a call ended) can still be
    // closing, and a slow one can miss its start: it is asked once more
    // before the person is told another app has it.
    if (classifyMediaError(err) === 'inUse') {
      await new Promise((resolve) => setTimeout(resolve, BUSY_RETRY_MS));
      try {
        return { track: await attempt(constraints), state: 'ok' };
      } catch (again) {
        return { track: null, state: classifyMediaError(again) };
      }
    }
    return { track: null, state: classifyMediaError(err) };
  }
}

/**
 * Asks for the microphone and camera. They are requested together so the
 * browser shows one prompt; if that fails, each is tried alone so that
 * blocking the camera still leaves a working microphone, and the other way
 * round.
 */
export async function acquire(options: AcquireOptions, gum?: GetUserMedia): Promise<Acquired> {
  const result: Acquired = { audioTrack: null, videoTrack: null, mic: options.wantAudio ? 'unavailable' : 'off', cam: options.wantVideo ? 'unavailable' : 'off' };
  if (!options.wantAudio && !options.wantVideo) return result;
  if (!gum) {
    if (!hasMediaDevices()) return result;
    gum = (c) => navigator.mediaDevices.getUserMedia(c);
  }
  const audio = audioConstraints(options.micId);
  const video = videoConstraints(options);

  if (options.wantAudio && options.wantVideo) {
    try {
      const stream = await gum({ audio, video });
      result.audioTrack = stream.getAudioTracks()[0] ?? null;
      result.videoTrack = stream.getVideoTracks()[0] ?? null;
      result.mic = result.audioTrack ? 'ok' : 'notFound';
      result.cam = result.videoTrack ? 'ok' : 'notFound';
      return result;
    } catch {
      // Fall through: find out which of the two is the problem.
    }
  }
  if (options.wantAudio) {
    const a = await one(gum, 'audio', audio);
    result.audioTrack = a.track;
    result.mic = a.state;
  }
  if (options.wantVideo) {
    const v = await one(gum, 'video', video);
    result.videoTrack = v.track;
    result.cam = v.state;
  }
  return result;
}

export function joinMode(mic: DeviceState, cam: DeviceState): JoinMode {
  const micUsable = mic === 'ok' || mic === 'off';
  const camUsable = cam === 'ok' || cam === 'off';
  if (micUsable && camUsable) return 'full';
  if (micUsable) return 'audioOnly';
  if (camUsable) return 'videoOnly';
  return 'listenOnly';
}

export interface PermissionNotice {
  tone: 'warning' | 'danger';
  title: string;
  body: string;
  /** What the person can do right now. */
  action: 'retry' | 'openSettings' | null;
  mode: JoinMode;
}

const failed = (s: DeviceState): s is FailedState => s !== 'ok' && s !== 'off';

function howToAllow(what: string, native: boolean, appName: string): string {
  return native
    ? `Open Settings, allow ${what} for ${appName}, then come back.`
    : `Select the camera or padlock icon in your browser's address bar, allow ${what}, then choose Try again.`;
}

/**
 * The banner shown on the pre-join screen and in the call when a device
 * could not be used. Returns null when there is nothing to explain.
 */
export function permissionNotice(mic: DeviceState, cam: DeviceState, native: boolean, appName: string): PermissionNotice | null {
  if (!failed(mic) && !failed(cam)) return null;
  const mode = joinMode(mic, cam);
  const settings = native ? 'openSettings' : 'retry';

  if (mic === 'unavailable' || cam === 'unavailable') {
    return {
      tone: 'danger',
      title: "This browser can't use the camera or microphone here",
      body: 'Camera and microphone only work on secure (https) pages in a supported browser. You can still join to listen.',
      action: null,
      mode: 'listenOnly',
    };
  }
  if (mic === 'denied' && cam === 'denied') {
    return {
      tone: 'danger',
      title: 'Camera and microphone are blocked',
      body: `You can still join to listen. To be seen and heard: ${howToAllow('camera and microphone', native, appName)}`,
      action: settings,
      mode,
    };
  }
  if (cam === 'inUse') {
    return {
      tone: 'warning',
      title: 'Camera is in use by another app',
      body:
        (failed(mic) ? 'Close the app or browser tab that is using it, then choose Try again.' : 'You can join with audio only. To use the camera, close the app or browser tab that is using it, then choose Try again.') +
        (mic === 'denied' ? ` Your microphone is blocked too: ${howToAllow('the microphone', native, appName)}` : ''),
      action: 'retry',
      mode,
    };
  }
  if (cam === 'denied') {
    return {
      tone: 'warning',
      title: 'Camera is blocked',
      body: `You can join with audio only. To turn the camera on later: ${howToAllow('the camera', native, appName)}`,
      action: settings,
      mode,
    };
  }
  if (mic === 'denied') {
    return {
      tone: 'danger',
      title: 'Microphone is blocked',
      body: `Others ${cam === 'ok' ? 'will see you but' : 'will'} not hear you. ${howToAllow('the microphone', native, appName)}`,
      action: settings,
      mode,
    };
  }
  if (mic === 'inUse') {
    return {
      tone: 'danger',
      title: 'Microphone is in use by another app',
      body: 'Close the app that is using it, then choose Try again. Until then others will not hear you.',
      action: 'retry',
      mode,
    };
  }
  if (mic === 'notFound' && cam === 'notFound') {
    return { tone: 'warning', title: 'No camera or microphone found', body: 'You can join to listen. Connect a headset or camera and choose Try again.', action: 'retry', mode };
  }
  if (cam === 'notFound') {
    return { tone: 'warning', title: 'No camera found', body: 'You can join with audio only. Connect a camera and choose Try again to be seen.', action: 'retry', mode };
  }
  return { tone: 'danger', title: 'No microphone found', body: 'Others will not hear you. Connect a microphone or headset and choose Try again.', action: 'retry', mode };
}

export interface DeviceLists {
  mics: MediaDeviceInfo[];
  cams: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
}

export async function listDevices(): Promise<DeviceLists> {
  const empty: DeviceLists = { mics: [], cams: [], speakers: [] };
  if (!hasMediaDevices() || typeof navigator.mediaDevices.enumerateDevices !== 'function') return empty;
  try {
    const all = await navigator.mediaDevices.enumerateDevices();
    // Before permission is granted, devices come back without IDs or labels.
    const named = all.filter((d) => d.deviceId !== '');
    return {
      mics: named.filter((d) => d.kind === 'audioinput'),
      cams: named.filter((d) => d.kind === 'videoinput'),
      speakers: named.filter((d) => d.kind === 'audiooutput'),
    };
  } catch {
    return empty;
  }
}

export function deviceLabel(device: MediaDeviceInfo, index: number, kind: string): string {
  return device.label || `${kind} ${index + 1}`;
}

/** True when the browser can route audio to a chosen speaker (not Safari or mobile). */
export function supportsSpeakerChoice(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

let sharedContext: AudioContext | null = null;
function audioContext(): AudioContext | null {
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  sharedContext ??= new Ctor();
  if (sharedContext.state === 'suspended') void sharedContext.resume().catch(() => {});
  return sharedContext;
}

/**
 * Reports the loudness of a microphone track, as numbers from 0 to 1:
 * `level` rises at once and falls away gradually, which reads well on a
 * meter; `raw` is the instantaneous value. Nothing is recorded or sent.
 * Returns a stop function.
 */
export function watchLevel(track: MediaStreamTrack, onLevel: (level: number, raw: number) => void): () => void {
  const ctx = audioContext();
  if (!ctx) return () => {};
  // A clone, so the meter keeps working while the sent track is muted.
  const clone = track.clone();
  clone.enabled = true;
  const source = ctx.createMediaStreamSource(new MediaStream([clone]));
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);
  const data = new Uint8Array(analyser.fftSize);
  let held = 0;
  const timer = window.setInterval(() => {
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (const v of data) sum += ((v - 128) / 128) ** 2;
    const raw = Math.min(1, Math.sqrt(sum / data.length) * 4);
    held = Math.max(raw, held * 0.8);
    onLevel(held < 0.02 ? 0 : held, raw);
  }, 60);
  return () => {
    window.clearInterval(timer);
    source.disconnect();
    clone.stop();
  };
}

/** The short two-note chime the host hears when someone asks to join (E3-S5). */
export function playChime(): void {
  const ctx = audioContext();
  if (!ctx) return;
  const now = ctx.currentTime;
  [660, 880].forEach((frequency, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, now + i * 0.16);
    gain.gain.exponentialRampToValueAtTime(0.12, now + i * 0.16 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.16 + 0.3);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + i * 0.16);
    osc.stop(now + i * 0.16 + 0.32);
  });
}
